import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import styles from './Calendar.scss'
import { EventRow } from './EventRow'
import { MonthPicker } from './MonthPicker'
import { Calendar as _Calendar } from 'common/Calendar'
import { Card } from 'common/styleguide/Card'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/SideNavLayout/TwoCol'
import { URLTabCard, TabCardItem } from 'common/styleguide/TabCard'
import { CalendarDate } from 'common/styleguide/CalendarDate'
import { IntroWidget } from 'common/IntroWidget'
import { EventListItem } from 'common/ListItems'
import { Icon } from 'common/Icon'
import { PopoverMenu, PopoverMenuItem } from 'common/styleguide/PopoverMenu'
import { CheckboxGroup } from 'common/styleguide/CheckboxGroup'
import { CreateEvent, CREATE_EVENT_DIALOG } from 'common/dialogs/CreateEvent'
import { CTA } from 'common/styleguide/CTA'
import { withCollapseState } from 'common/layouts/SideNavLayout/SideNavContexts'
import { CircleSpinner } from 'common/spinners/CircleSpinner'
import { IeNudgeBar } from 'common/IeNudgeBar'
import * as sparseTripsActions from 'lib/api/sparseTrips'
import * as eventsActions from 'lib/api/events'
import { isTrip, isEvent, firstVisibleDay, lastVisibleDay, isPast, indexEventsByDate } from 'lib/calendarUtils'
import { queryStringSet } from 'lib/utils'
import * as userActions from 'lib/api/currentUser'
import { Msg, fm, featureNames, commonActions, commonMetrics } from 'lib/localization'
import moment from 'moment'
import _ from 'lodash'
import { Set, Map, OrderedMap } from 'immutable'
import Helmet from 'react-helmet'
import { FormattedMessage as T, injectIntl } from 'react-intl'

class _CalendarPage extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      dialog: null,
      calendarEventsByDate: Map(),
      eventsLoaded: false
      // structure of calendarEventsByDate:
      // Map keyed by date => map keyed by object id
      // this is needed to dedupe items that are loaded twice
    }
  }

  componentDidMount () {
    const { currentUser } = this.props
    // we need to load a large number of events (unbound by current date) to populate events card
    this.props.loadEvents({ per_page: 200, user_id: currentUser.id })
      .then(({ value: { results } }) => {
        this.addCalendarEventsToState(results)
        this.setState({ eventsLoaded: true })
      })

    // we need to load events here even it it may be redundant because
    // the calendar can be pointing to a date several months in the past
    // via the URL params
    this.loadMonthData()
  }

  componentDidUpdate (prevProps, prevState) {
    let shouldLoadData = prevProps.location.query.date !== this.props.location.query.date

    shouldLoadData = shouldLoadData || (!this.userCalendarPreferences(prevProps.currentUser).has('heartRateZones') && this.userCalendarPreferences(this.props.currentUser).has('heartRateZones'))

    shouldLoadData && this.loadMonthData()
  }

  getDate () {
    if (this.props.location.query.date) {
      try {
        return moment(this.props.location.query.date, 'YYYY-M')
      } catch (e) {
        // date parse error
        return moment()
      }
    }
    // default fallback
    return moment()
  }

  addCalendarEventsToState (newEvents) {
    const { calendarEventsByDate } = this.state

    const newIndex = indexEventsByDate(newEvents, startAccessor, endAccessor)
    const withNewData = calendarEventsByDate.mergeDeep(newIndex)

    this.setState({
      calendarEventsByDate: withNewData
    })
  }

  // data isn't being loaded for the initial render!
  loadMonthData = (opts = {}) => {
    const { loadAllTrips, loadAllEvents, currentUser } = this.props

    const activeFields = this.getActiveFields()

    const date = this.getDate()
    const start = firstVisibleDay(date)
    const end = lastVisibleDay(date)

    const cb = ({ value: { results } }) => { this.addCalendarEventsToState(results) }

    const minDateStr = moment(start).subtract(1, 'days').format('YYYY-M-D')
    const maxDateStr = moment(end).add(1, 'days').format('YYYY-M-D')

    // We're going to add a day on both ends, that way any timezone differences will be accounted for
    const tripParams = {
      user_id: currentUser.id,
      departed_at_max: maxDateStr,
      departed_at_min: minDateStr,
      hr_zones: activeFields.has('heartRateZones'),
      include_exclude_from_totals: true
    }
    loadAllTrips(tripParams, cb)

    const eventParams = {
      user_id: currentUser.id,
      starts_on_max: maxDateStr,
      starts_on_min: minDateStr
    }
    loadAllEvents(eventParams, cb)
  }

  dateChanged = (date) => {
    queryStringSet(this.props.location, 'date', date.format('YYYY-M'))
  }

  showDialog = () => {
    this.setState({ dialog: CREATE_EVENT_DIALOG })
  }

  hideDialog = () => {
    this.setState({ dialog: null })
  }

  getActiveFields = () => {
    const { currentUser } = this.props
    const prefs = this.userCalendarPreferences(currentUser)
    return prefs.add('name')
  }

  // translates the legacy calendar preferences
  userCalendarPreferences = (currentUser) => {
    const legacyPrefsAndDefaults = {
      'calendar_show_distance': true,
      'calendar_show_elevation': true,
      'calendar_show_duration': true,
      'calendar_show_avg_speed': false,
      'calendar_show_power': true,
      'calendar_show_calories': true,
      'calendar_show_hr': currentUser.maxHR !== undefined,
      'calendar_show_hr_zones': false
    }

    return Set(_.compact(_.map(legacyPrefsAndDefaults, (defaultValue, preference) => {
      const p = currentUser.preferences.get(preference)
      if (p) {
        return calFieldMap[preference]
      } else if (p === undefined && defaultValue) {
        return calFieldMap[preference]
      } else {
        return false
      }
    })))
  }

  // we have the old active fields and the new ones coming from the popover
  // loop through and setPreference accordingly
  onChangeFields = (newActiveFields) => {
    const { setPreference, intl } = this.props

    const activeFields = this.getActiveFields()

    availableFields(intl).forEach((human, field) => {
      if (activeFields.get(field) && newActiveFields.get(field)) {
        // they equal! great
      } else if (activeFields.get(field) && !newActiveFields.get(field)) {
        setPreference(_.invert(calFieldMap)[field], false)
      } else if (!activeFields.get(field) && newActiveFields.get(field)) {
        setPreference(_.invert(calFieldMap)[field], true)
      }
    })
  }

  render () {
    const date = this.getDate()
    const { dialog, calendarEventsByDate, eventsLoaded } = this.state
    const { intl, events, createEvent, collapseState, currentUser, setPreference } = this.props
    const isMobile = collapseState === 'viewport' || collapseState === 'viewport-expanded'

    const { false: notPastEvents, true: pastEvents } = { true: [], false: [], ..._.groupBy(events, isPast) }

    const activeFields = this.getActiveFields()

    return (
      <div className={styles.Calendar}>
        <Helmet title={`${fm(intl, featureNames.calendar)} · Ride with GPS`} />
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        {dialog === CREATE_EVENT_DIALOG && <CreateEvent onSubmit={createEvent} onRequestClose={() => this.hideDialog()} />}
        {!isMobile && <ColWrap>
          <Card noPadding className={styles.fullWidth}>
            <_Calendar
              events={calendarEventsByDate}
              onChange={this.dateChanged}
              startAccessor={startAccessor}
              endAccessor={endAccessor}
              date={date}
              activeFields={activeFields} // TODO this is just for debugging
              RowComponent={EventRow}
              toolbarLeft={<CreateEventLink showDialog={this.showDialog} />}
              toolbarCenter={<MonthPicker dateChanged={this.dateChanged} date={date}>{date.format('MMMM YYYY')}</MonthPicker>}
              toolbarRight={<SettingsAndExport intl={intl} currentUser={currentUser} activeFields={activeFields} onChangeFields={this.onChangeFields} />} />
          </Card>
        </ColWrap>}
        <ColWrap>
          <MainCol id='eventsCard'>
            {/* The key here is a convenient way to make the TabCard update its internal selected tab when we change the titles out from under it */}
            <URLTabCard name='eventsTab' key={isMobile ? 'mobile' : 'notMobile'}>
              <TabCardItem id='events' title={<T id='calendar.events' defaultMessage='Events' />}>
                <EventList
                  includePhoto={!isMobile}
                  eventsLoaded={eventsLoaded}
                  emptyText={<T id='calendar.noUpcomingEvents' defaultMessage='You have no upcoming events. Use the buttons to the right to find events to join or to create your own events.' />}
                  events={_.sortBy(notPastEvents, ['startsOn', 'name'])} />
              </TabCardItem>
              <TabCardItem id='past' title={<T id='calendar.pastEvents' defaultMessage='Past Events' />}>
                <EventList
                  includePhoto={!isMobile}
                  eventsLoaded={eventsLoaded}
                  emptyText={<T id='calendar.noPastEvents' defaultMessage='You have no past events.' />}
                  events={_.orderBy(pastEvents, ['startsOn', 'name'], ['desc', 'asc'])} />
              </TabCardItem>
            </URLTabCard>
          </MainCol>
          <SecondaryCol>
            <h2 data-heading-alt><T id='calendar.newEvent' defaultMessage='New Event' /></h2>
            <Card>
              <CTA secondary margins={false} onClick={this.showDialog}><T id='calendar.createAnEvent' defaultMessage='Create an Event' /></CTA>
              <CTA link margins={false} href='/find?search[events]=on' className={styles.findEventCTA}><T id='calendar.findAnEvent' defaultMessage='Find an Event' /></CTA>
            </Card>
          </SecondaryCol>
        </ColWrap>
      </div>
    )
  }
}

export const CalendarPage = withCollapseState(_CalendarPage)

const CreateEventLink = ({ showDialog }) => (
  <div className={styles.actionContainer}>
    <a onClick={showDialog} className={styles.toolbarAction}>
      <Icon i='md-create' /> <T id='calendar.createEvent' defaultMessage='Create Event' />
    </a>
  </div>
)

const availableFields = intl => OrderedMap({
  distance: fm(intl, commonMetrics.distance),
  elevation: fm(intl, commonMetrics.elevation),
  movingTime: fm(intl, commonMetrics.movingTime),
  avgSpeed: fm(intl, commonMetrics.avgSpeed),
  avgWatts: fm(intl, commonMetrics.power),
  calories: fm(intl, commonMetrics.calories),
  heartRate: fm(intl, commonMetrics.heartRate),
  heartRateZones: fm(intl, commonMetrics.heartRateZones, { count: 0 })
})

const calFieldMap = {
  'calendar_show_distance': 'distance',
  'calendar_show_elevation': 'elevation',
  'calendar_show_duration': 'movingTime',
  'calendar_show_avg_speed': 'avgSpeed',
  'calendar_show_power': 'avgWatts',
  'calendar_show_calories': 'calories',
  'calendar_show_hr': 'heartRate',
  'calendar_show_hr_zones': 'heartRateZones'
}

class SettingsAndExport extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      settingsExpanded: false
    }
  }

  toggleSettingsExpanded = () => {
    this.setState({ settingsExpanded: !this.state.settingsExpanded })
  }

  render () {
    const { intl, currentUser, activeFields, onChangeFields } = this.props
    const { settingsExpanded } = this.state

    return (
      <Fragment>
        <a ref={r => { this._settingsLink = r }} onClick={this.toggleSettingsExpanded} className={styles.toolbarAction}>
          <Icon i='md-settings' /> <Msg m={featureNames.settings} />
        </a>
        {settingsExpanded && <PopoverMenu
          target={this._settingsLink}
          container={window.body}
          placement='bottom'
          onRequestClose={this.toggleSettingsExpanded}>
          <PopoverMenuItem>
            <CheckboxGroup
              style={{ margin: '0 0' }}
              horizontal
              options={availableFields(intl)}
              selected={activeFields}
              onChange={onChangeFields} />
          </PopoverMenuItem>
        </PopoverMenu>}
        <a data-tip title={fm(intl, commonActions.exportCSVDesc)} className={styles.toolbarAction} href={`/users/${currentUser.id}/trips.csv`}>
          <Icon i='md-download' /><Msg m={commonActions.exportCSV} />
        </a>
      </Fragment>
    )
  }
}

const EventList = ({ events, emptyText, includePhoto, eventsLoaded }) => {
  if (!eventsLoaded) {
    return <div className={styles.eventsLoading}>
      <CircleSpinner size={30} />
    </div>
  }

  if (events.length === 0) {
    return <div className={styles.NotAList}>
      <IntroWidget
        left={<div><CalendarDate large date={new Date()} /></div>}
        link={<a href='/find'><T id='calendar.findEvents' defaultMessage='Find Events' /></a>}>
        {emptyText}
      </IntroWidget>
    </div>
  }
  return <div className={styles.EventList}>
    {events.map(e => <EventListItem key={e.id} event={e} includePhoto={includePhoto} />)}
  </div>
}

const startAccessor = (obj) => {
  if (isTrip(obj)) {
    return obj.departedAt
  }

  if (isEvent(obj)) {
    return obj.startsOn
  }

  console.warn('Unable to determine a start date for given event object')
}

const endAccessor = (obj) => {
  if (isTrip(obj)) {
    return null // currently this will just act as a noop
  }

  if (isEvent(obj)) {
    return obj.endsOn
  }

  console.warn('Unable to determine an end date for a given event object')
}

const mapDispatchToProps = {
  loadAllTrips: sparseTripsActions.loadAllTrips,
  loadEvents: eventsActions.loadEvents,
  loadAllEvents: eventsActions.loadAllEvents,
  createEvent: eventsActions.createEvent,
  setPreference: userActions.setPreference
}

const mapStateToProps = (state) => {
  const trips = state.sparseTrips.valueSeq().toArray()
  const events = state.events.get('events').valueSeq().toArray()

  return {
    events,
    trips,
    currentUser: state.currentUser
  }
}

export const Calendar = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(CalendarPage))
